import React, { useLayoutEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import imageCompression from "browser-image-compression";
import {
  FaSchool,
  FaIdCard,
  FaMapMarkerAlt,
  FaArrowLeft,
  FaFilePdf,
  FaLock,
  FaBullhorn,
  FaUsers,
  FaRecycle,
  FaCamera,
  FaUserGraduate,
  FaChalkboardTeacher,
  FaImage,
  FaExpand,
  FaTimes,
  FaBus,
} from "react-icons/fa";
import axiosConn from "config/axiosConn";
import { encryptPdfForTransport } from "../../../utils/fileCrypto";
import { processGeotaggedImage } from "../geotaggedUtils";
import { useSubmitSwachhataSurveyMutation } from "../../../redux/auth/authApi";
import {
  SURVEY_TITLE,
  SURVEY_TAGLINE,
  PHOTOS_MIN,
  PHOTOS_MAX,
  PHOTO_MAX_MB,
  VISIT_PHOTOS_MIN,
  VISIT_PHOTOS_MAX,
  VISIT_DESCRIPTION_MAX,
  BINS_OPTIONS,
  YES_NO,
  COMPOST_OPTIONS,
  AWARENESS_TYPES,
  COMPETITIONS,
  REDUCTION_ACTIVITIES,
  QUESTIONS,
} from "./swachhataSurvey";
import shsLogo from "../../../assets/images/swachhata/shs-logo-2026.png";
import "../EkPed3Modal.css";
import "./SwachhataSurvey.css";

/*
 * Swachhata Hi Seva 2026 — school SWM report form (Q1–Q10), embedded in the
 * popup. Files go through the existing `upload-collaboration-file` endpoint
 * (JPG / JPEG / PNG / PDF) and the answers through `swachhata/survey/submit`,
 * which re-validates everything and issues the certificate on the first save.
 *
 * Errors are shown inline on the question card (and in the sticky footer) —
 * toasts render behind the full-screen popup.
 *
 * Mixed text is always ONE template string: the Anuvadini translation
 * extension merges adjacent JSX text nodes and drops the spaces.
 */

const EMPTY = {
  binsInstalled: "",
  awarenessConducted: "",
  awarenessTypes: [],
  awarenessOther: "",
  competitions: [],
  competitionOther: "",
  students: "",
  teachers: "",
  community: "",
  wasteAudit: "",
  wasteReduction: "",
  reductionActivities: [],
  reductionOther: "",
  wetWasteComposted: "",
  exposureVisit: "",
  visitPlace: "",
  visitDescription: "",
  visitStudents: "",
  visitTeachers: "",
};

const numText = (v) => (v == null ? "" : String(v));

const fromAnswers = (a) =>
  a
    ? {
        ...EMPTY,
        binsInstalled: a.binsInstalled || "",
        awarenessConducted: a.awarenessConducted || "",
        awarenessTypes: a.awarenessTypes || [],
        awarenessOther: a.awarenessOther || "",
        competitions: a.competitions || [],
        competitionOther: a.competitionOther || "",
        students: numText(a.students),
        teachers: numText(a.teachers),
        community: numText(a.community),
        wasteAudit: a.wasteAudit || "",
        wasteReduction: a.wasteReduction || "",
        reductionActivities: a.reductionActivities || [],
        reductionOther: a.reductionOther || "",
        wetWasteComposted: a.wetWasteComposted || "",
        exposureVisit: a.exposureVisit || "",
        visitPlace: a.visitPlace || "",
        visitDescription: a.visitDescription || "",
        visitStudents: numText(a.visitStudents),
        visitTeachers: numText(a.visitTeachers),
      }
    : EMPTY;

const QIDS = ["Q1", "Q2", "Q3", "Q4", "Q5", "Q6", "Q7", "Q8", "Q9", "Q10", "Q11"];

const listDone = (list, other) => list.length > 0 && (!list.includes("other") || other.trim() !== "");

// Q11 is complete on "No", or on "Yes" with every visit detail filled in.
const visitDone = (f, visitPhotos) =>
  f.exposureVisit === "no" ||
  (f.exposureVisit === "yes" &&
    f.visitPlace.trim() !== "" &&
    f.visitDescription.trim() !== "" &&
    f.visitStudents !== "" &&
    f.visitTeachers !== "" &&
    visitPhotos.length >= VISIT_PHOTOS_MIN);

function progressOf(f, photos, visitPhotos = []) {
  return {
    Q1: !!f.binsInstalled,
    Q2: f.awarenessConducted === "no" || (f.awarenessConducted === "yes" && listDone(f.awarenessTypes, f.awarenessOther)),
    Q3: listDone(f.competitions, f.competitionOther),
    Q4: f.students !== "",
    Q5: f.teachers !== "",
    Q6: f.community !== "",
    Q7: !!f.wasteAudit,
    Q8: f.wasteReduction === "no" || (f.wasteReduction === "yes" && listDone(f.reductionActivities, f.reductionOther)),
    Q9: !!f.wetWasteComposted,
    Q10: photos.length >= PHOTOS_MIN,
    Q11: visitDone(f, visitPhotos),
  };
}

// Mirrors parseSurvey() in SwachhataHiSevaController.js — first problem wins.
// `q` is the card to scroll to, `label` what the footer calls it.
function validate(f, photos, visitPhotos = []) {
  const fail = (q, msg, label = q) => ({ q, label, msg, kind: "validate" });
  if (!f.binsInstalled) return fail("Q1", "Please tell us whether the four bins are installed.");
  if (!f.awarenessConducted) return fail("Q2", "Please tell us whether an awareness activity was conducted.");
  if (f.awarenessConducted === "yes") {
    if (!f.awarenessTypes.length) return fail("Q2", "Select at least one type of awareness activity.", "Q2A");
    if (f.awarenessTypes.includes("other") && !f.awarenessOther.trim()) return fail("Q2", "Describe the other awareness activity.", "Q2A");
  }
  if (!f.competitions.length) return fail("Q3", "Select at least one competition or activity.");
  if (f.competitions.includes("other") && !f.competitionOther.trim()) return fail("Q3", "Describe the other competition or activity.");
  if (f.students === "") return fail("Q4", "Enter the number of students who participated (0 if none).");
  if (f.teachers === "") return fail("Q5", "Enter the number of teachers who participated (0 if none).");
  if (f.community === "") return fail("Q6", "Enter the number of community members who participated (0 if none).");
  if (!f.wasteAudit) return fail("Q7", "Please tell us whether a solid waste audit was conducted.");
  if (!f.wasteReduction) return fail("Q8", "Please tell us whether any waste reduction, reuse, recycling or processing activity was undertaken.");
  if (f.wasteReduction === "yes") {
    if (!f.reductionActivities.length) return fail("Q8", "Select at least one activity.", "Q8A");
    if (f.reductionActivities.includes("other") && !f.reductionOther.trim()) return fail("Q8", "Describe the other activity.", "Q8A");
  }
  if (!f.wetWasteComposted) return fail("Q9", "Please tell us whether wet waste is composted in the school.");
  if (photos.length < PHOTOS_MIN) return fail("Q10", `Upload at least ${PHOTOS_MIN} photographs of campaign activities.`);
  if (!f.exposureVisit) return fail("Q11", "Please tell us whether an exposure visit was conducted for students.");
  if (f.exposureVisit === "yes") {
    if (!f.visitPlace.trim()) return fail("Q11", "Enter the place of the visit.");
    if (!f.visitDescription.trim()) return fail("Q11", "Describe the visit.");
    if (f.visitStudents === "") return fail("Q11", "Enter the number of students who took part (0 if none).");
    if (f.visitTeachers === "") return fail("Q11", "Enter the number of teachers who took part (0 if none).");
    if (visitPhotos.length < VISIT_PHOTOS_MIN) return fail("Q11", "Upload at least one geo-tagged photograph of the visit.");
  }
  return null;
}

const reducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// ── Artwork ──────────────────────────────────────────────────────────────────

const Tick = () => (
  <svg viewBox="0 0 24 24" aria-hidden="true">
    <path d="M5 12.5 10 17l9-10" />
  </svg>
);

const TILE_ICONS = {
  yes: <path d="M5 12.5 10 17l9-10" />,
  no: <path d="M7 7l10 10M17 7 7 17" />,
  na: <path d="M6 12h12" />,
};

const LEAVES = [
  // Kept to the edges and the wave band so none drifts across the copy.
  { "--x": "3%", "--y": "74%", "--s": "14px", "--c": "#8be07a", "--t": "7s", "--dl": "0s" },
  { "--x": "26%", "--y": "5%", "--s": "10px", "--c": "#ffd08a", "--t": "9s", "--dl": "-2s" },
  { "--x": "62%", "--y": "4%", "--s": "12px", "--c": "#b6f0a8", "--t": "8s", "--dl": "-4s" },
  { "--x": "70%", "--y": "86%", "--s": "9px", "--c": "#ffffff", "--t": "10s", "--dl": "-1s" },
  { "--x": "97%", "--y": "46%", "--s": "15px", "--c": "#9be36d", "--t": "11s", "--dl": "-6s" },
  { "--x": "42%", "--y": "90%", "--s": "8px", "--c": "#ffe29a", "--t": "6s", "--dl": "-3s" },
];

const HERO_BINS = [
  { c: "#22a447", item: <ellipse rx="7" ry="3.6" fill="#a6ee74" /> },
  {
    c: "#1e6fd9",
    item: (
      <g>
        <rect x="-3.4" y="-8" width="6.8" height="15" rx="2.2" fill="#c6e6ff" />
        <rect x="-1.8" y="-10.8" width="3.6" height="3.4" rx="1" fill="#6db3f2" />
      </g>
    ),
  },
  { c: "#d93a3a", item: <rect x="-5.5" y="-4.5" width="11" height="9" rx="2.4" fill="#fff" stroke="#f4b5b5" strokeWidth="1.2" /> },
  {
    c: "#2e3a33",
    item: (
      <g>
        <rect x="-3.8" y="-7" width="7.6" height="14" rx="1.6" fill="#ffd166" />
        <rect x="-1.6" y="-9.2" width="3.2" height="2.4" fill="#d99a00" />
      </g>
    ),
  },
];

function HeroBins() {
  return (
    <svg className="shsf-hero__art" viewBox="0 0 250 150" aria-hidden="true">
      <ellipse cx="125" cy="139" rx="118" ry="8" fill="rgba(0,0,0,0.18)" />
      {HERO_BINS.map((b, k) => (
        <g key={b.c} transform={`translate(${12 + k * 58} 0)`} style={{ "--d": `${k * 1.2}s` }}>
          <g transform="translate(22 70)">
            <g className="shsf-hero__item">{b.item}</g>
          </g>
          <g className="shsf-hero__bin">
            <g className="shsf-hero__lid">
              <rect x="15" y="62" width="14" height="6" rx="2" fill={b.c} />
              <rect x="-2" y="67" width="48" height="9" rx="3.5" fill={b.c} />
              <rect x="0" y="68" width="44" height="2.6" rx="1.3" fill="rgba(255,255,255,0.28)" />
            </g>
            <path d="M1 78h42l-4.5 58h-33Z" fill={b.c} />
            <path d="M1 78h42l-0.6 7H1.6Z" fill="rgba(0,0,0,0.16)" />
            <g className="shsf-hero__eyes">
              <circle cx="15" cy="101" r="5" fill="#fff" />
              <circle cx="29" cy="101" r="5" fill="#fff" />
              <circle cx="15.6" cy="102" r="2.4" fill="#16261c" />
              <circle cx="29.6" cy="102" r="2.4" fill="#16261c" />
            </g>
            <path d="M16 113q6 5 12 0" stroke="#fff" strokeWidth="2.4" fill="none" strokeLinecap="round" />
          </g>
        </g>
      ))}
    </svg>
  );
}

// One sine period every 600 units, drawn twice across 2400 so a -50% slide loops.
const WAVE_PATH = `M0 30 ${Array.from({ length: 4 }, (_, k) => `C${k * 600 + 100} 0 ${k * 600 + 200} 0 ${k * 600 + 300} 30 C${k * 600 + 400} 60 ${k * 600 + 500} 60 ${k * 600 + 600} 30`).join(" ")} V80 H0Z`;

const BIN_COLOURS = ["#22a447", "#1e6fd9", "#d93a3a", "#33403a"];

function BinsArt({ empty }) {
  return (
    <svg viewBox="0 0 108 60" aria-hidden="true" className="shsf-bins-art">
      {BIN_COLOURS.map((c, k) => {
        const paint = empty ? { fill: "none", stroke: "#b3c5b8", strokeWidth: 1.5, strokeDasharray: "3 2.4" } : { fill: c };
        return (
          <g key={c} transform={`translate(${3 + k * 26} 0)`}>
            <g className="shsf-bin" style={{ "--d": `${k * 0.09}s` }}>
              <g className="shsf-bin__lid">
                <rect x="7" y="6" width="8" height="4" rx="1.2" {...paint} />
                <rect x="0" y="10" width="22" height="5" rx="2" {...paint} />
              </g>
              <path d="M1.5 17h19l-2.2 36H3.7Z" {...paint} />
              {!empty && <path d="M7 23v24M11 23v24M15 23v24" stroke="rgba(255,255,255,0.38)" strokeWidth="1.6" strokeLinecap="round" />}
            </g>
          </g>
        );
      })}
      <path d="M1 56.5h106" stroke="#cfe0d3" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

const BIN_NOTES = {
  all_four: "Wet, dry, sanitary and special waste bins are in place",
  not_installed: "The school does not have all four bins yet",
};

// ── Inputs ───────────────────────────────────────────────────────────────────

function BinTiles({ value, onChange }) {
  return (
    <div className="shsf-bintiles" role="radiogroup">
      {BINS_OPTIONS.map((o) => {
        const on = value === o.key;
        const empty = o.key !== "all_four";
        return (
          <label key={o.key} className={`shsf-bintile ${empty ? "shsf-bintile--none" : ""} ${on ? "is-on" : ""}`}>
            <input type="radio" name="binsInstalled" value={o.key} checked={on} onChange={() => onChange(o.key)} />
            <span className="shsf-bintile__art">
              <BinsArt empty={empty} />
            </span>
            <span className="shsf-bintile__text">
              <b>{o.label}</b>
              <small>{BIN_NOTES[o.key]}</small>
            </span>
            <span className="shsf-bintile__mark" aria-hidden="true">
              <Tick />
            </span>
          </label>
        );
      })}
    </div>
  );
}

function Tiles({ name, options, value, onChange }) {
  return (
    <div className="shsf-tiles" role="radiogroup">
      {options.map((o) => {
        const on = value === o.key;
        return (
          <label key={o.key} className={`shsf-tile shsf-tile--${o.key} ${on ? "is-on" : ""}`}>
            <input type="radio" name={name} value={o.key} checked={on} onChange={() => onChange(o.key)} />
            <span className="shsf-tile__icon" aria-hidden="true">
              <svg viewBox="0 0 24 24">{TILE_ICONS[o.key]}</svg>
            </span>
            <span>{o.label}</span>
          </label>
        );
      })}
    </div>
  );
}

function Chips({ options, value, onChange }) {
  return (
    <div className="shsf-chips" role="group">
      {options.map((o) => {
        const on = value.includes(o.key);
        return (
          <label key={o.key} className={`shsf-chip ${on ? "is-on" : ""}`}>
            <input type="checkbox" checked={on} onChange={() => onChange(on ? value.filter((k) => k !== o.key) : [...value, o.key])} />
            <span className="shsf-chip__box" aria-hidden="true">
              <Tick />
            </span>
            <span>{o.label}</span>
          </label>
        );
      })}
    </div>
  );
}

function OtherInput({ value, onChange, placeholder }) {
  return (
    <div className="shsf-other">
      <input className="shsf-input" type="text" maxLength={200} placeholder={placeholder} value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

function Question({ id, n, text, hint, done, error, errorKey, sub = false, deco = null, className = "", children }) {
  return (
    <div
      id={`shsf-${id}`}
      className={`shsf-q ${sub ? "shsf-q--sub" : "shsf-reveal"} ${done ? "is-done" : ""} ${error ? "is-error" : ""} ${deco ? "has-deco" : ""} ${className}`}
    >
      {deco}
      <div className="shsf-q__head">
        <span className="shsf-q__num">
          {n}
          <span className="shsf-q__tick" aria-hidden="true">
            <Tick />
          </span>
        </span>
        <div className="shsf-q__titles">
          <div className="shsf-q__text">
            {text}
            <span className="shsf-req">*</span>
          </div>
          {hint && <div className="shsf-q__hint">{hint}</div>}
        </div>
      </div>
      <div className="shsf-q__body">{children}</div>
      {error && (
        <div key={errorKey} className="shsf-q__error" role="alert">
          <span aria-hidden="true">!</span>
          {error}
        </div>
      )}
    </div>
  );
}

function Section({ step, title, icon, ids, progress, children }) {
  const done = ids.filter((q) => progress[q]).length;
  return (
    <div className="shsf-section">
      <div className="shsf-section__head shsf-reveal">
        <span className="shsf-section__icon" aria-hidden="true">
          {icon}
        </span>
        <span className="shsf-section__titles">
          <span className="shsf-section__step">{step}</span>
          <h2 className="shsf-section__title">{title}</h2>
        </span>
        <span className="shsf-section__line" aria-hidden="true" />
        <span className={`shsf-section__count ${done === ids.length ? "is-done" : ""}`}>{`${done} of ${ids.length}`}</span>
      </div>
      {children}
    </div>
  );
}

const COUNTS = [
  { id: "Q4", key: "students", text: QUESTIONS.q4, Icon: FaUserGraduate, tone: "green" },
  { id: "Q5", key: "teachers", text: QUESTIONS.q5, Icon: FaChalkboardTeacher, tone: "orange" },
  { id: "Q6", key: "community", text: QUESTIONS.q6, Icon: FaUsers, tone: "blue" },
];

// ── Form ─────────────────────────────────────────────────────────────────────

export default function SwachhataSurveyForm({ answers = null, onBack, backLabel = "Back", onSuccess }) {
  const userData = useSelector((state) => state.auth.user);
  const isUpdate = !!answers;
  const [form, setForm] = useState(() => fromAnswers(answers));
  const [photos, setPhotos] = useState(() => (answers?.photos || []).map((p) => ({ url: p.url, name: p.name || "", type: p.type || "image" })));
  const [uploading, setUploading] = useState(0); // number of files in flight
  const [visitPhotos, setVisitPhotos] = useState(() =>
    (answers?.visitPhotos || []).map((p) => ({ url: p.url, name: p.name || "", latitude: p.latitude, longitude: p.longitude, geoTagAdded: !!p.geoTagAdded }))
  );
  const [visitUploading, setVisitUploading] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [formError, setFormError] = useState(null); // { q, label, msg, kind }
  const [nonce, setNonce] = useState(0);
  const [armed, setArmed] = useState(false);
  const rootRef = useRef(null);
  const [submitSurvey, { isLoading: submitting }] = useSubmitSwachhataSurveyMutation();

  // Scroll reveal. `data-shown` is set outside React so re-renders never wipe it.
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root || typeof IntersectionObserver === "undefined" || reducedMotion()) return undefined;
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((en) => {
          if (en.isIntersecting) {
            en.target.setAttribute("data-shown", "");
            io.unobserve(en.target);
          }
        }),
      { root: root.closest(".ekped3-form-modal"), threshold: 0.05, rootMargin: "0px 0px -24px 0px" }
    );
    root.querySelectorAll(".shsf-reveal").forEach((el) => io.observe(el));
    setArmed(true);
    return () => io.disconnect();
  }, []);

  const progress = progressOf(form, photos, visitPhotos);
  const done = QIDS.filter((q) => progress[q]).length;
  const complete = done === QIDS.length;
  const pct = Math.round((done / QIDS.length) * 100);
  // A validation message disappears as soon as its question is answered.
  const shownError = formError && !(formError.kind === "validate" && progress[formError.q]) ? formError : null;
  const errorFor = (q) => (shownError && shownError.q === q ? shownError.msg : null);

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));
  const setCount = (key) => (e) => {
    const digits = e.target.value.replace(/[^0-9]/g, "").slice(0, 7);
    setForm((f) => ({ ...f, [key]: digits === "" ? "" : String(parseInt(digits, 10)) }));
  };
  const bump = (key, delta) =>
    setForm((f) => {
      const cur = f[key] === "" ? 0 : parseInt(f[key], 10);
      return { ...f, [key]: String(Math.max(0, Math.min(9999999, cur + delta))) };
    });

  const jump = (q) => {
    const el = document.getElementById(`shsf-${q}`);
    if (!el) return;
    el.setAttribute("data-shown", "");
    el.scrollIntoView({ behavior: reducedMotion() ? "auto" : "smooth", block: "center" });
    el.removeAttribute("data-flash");
    void el.offsetWidth; // restart the flash animation
    el.setAttribute("data-flash", "");
    setTimeout(() => el.removeAttribute("data-flash"), 1400);
  };

  const raise = (err) => {
    setFormError(err);
    setNonce((n) => n + 1);
  };

  const schoolName = userData?.school_name || "—";
  const udise = userData?.udise_id || "—";
  const place = [userData?.state, userData?.district].filter(Boolean).join(" / ") || "—";

  // ── Q10 uploads ────────────────────────────────────────────────────────────
  const addFiles = async (files) => {
    if (!files.length || uploading) return;
    const remaining = PHOTOS_MAX - photos.length;
    if (remaining <= 0) {
      raise({ q: "Q10", label: "Q10", msg: `You can upload at most ${PHOTOS_MAX} files. Remove one to add another.`, kind: "upload" });
      return;
    }
    const picked = files.slice(0, remaining);
    setFormError(
      files.length > remaining
        ? { q: "Q10", label: "Q10", msg: `Only ${remaining} more file(s) can be added — the first ${remaining} were taken.`, kind: "upload" }
        : null
    );
    setUploading(picked.length);
    try {
      // Images share one request; each PDF goes alone, AES-wrapped like the
      // dashboard's notification upload (the transport IV travels per request).
      const images = new FormData();
      const imageMeta = [];
      const pdfs = [];
      for (const file of picked) {
        const ext = (file.name.split(".").pop() || "").toLowerCase();
        const isPdf = file.type === "application/pdf" && ext === "pdf";
        const isImage = ["image/jpeg", "image/jpg", "image/png"].includes(file.type) && ["jpg", "jpeg", "png"].includes(ext);
        if (!isPdf && !isImage) throw new Error(`"${file.name}" is not allowed. Upload JPG, JPEG, PNG or PDF files only.`);
        let out = file;
        if (file.size > PHOTO_MAX_MB * 1024 * 1024) {
          if (isPdf) throw new Error(`"${file.name}" is larger than ${PHOTO_MAX_MB} MB. Please upload a smaller PDF.`);
          out = await imageCompression(file, { maxSizeMB: PHOTO_MAX_MB, maxWidthOrHeight: 2560, useWebWorker: true });
        }
        const base = file.name.replace(/\.[^.]+$/, "").replace(/[^a-zA-Z0-9_-]+/g, "_").slice(0, 40) || "photo";
        const stamp = new Date().toISOString().replace(/[:.]/g, "-");
        const renamed = new File([out], `${base}_${stamp}_shs26.${ext}`, { type: file.type });
        if (isPdf) {
          pdfs.push({ file: renamed, name: file.name });
        } else {
          images.append("files", renamed);
          imageMeta.push({ name: file.name, type: "image" });
        }
      }

      const upload = async (formData) => {
        const res = await axiosConn("post", "upload-collaboration-file", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const urls = res?.data?.status === "success" ? res.data.data || [] : [];
        if (!urls.length) throw new Error(res?.data?.message || "Upload failed. Please try again.");
        return urls;
      };

      const added = [];
      if (imageMeta.length) {
        const urls = await upload(images);
        urls.forEach((url, i) => added.push({ url, ...imageMeta[i] }));
      }
      for (const pdf of pdfs) {
        const { encryptedFile, metadata } = await encryptPdfForTransport(pdf.file);
        const formData = new FormData();
        Object.entries(metadata).forEach(([key, value]) => formData.append(key, value));
        formData.append("files", encryptedFile);
        const [url] = await upload(formData);
        added.push({ url, name: pdf.name, type: "pdf" });
      }
      setPhotos((prev) => [...prev, ...added].slice(0, PHOTOS_MAX));
    } catch (err) {
      raise({ q: "Q10", label: "Q10", msg: err?.response?.data?.message || err?.message || "Upload failed. Please try again.", kind: "upload" });
    } finally {
      setUploading(0);
    }
  };

  const handleFiles = (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    addFiles(files);
  };

  const removePhoto = (idx) => {
    setPhotos((prev) => prev.filter((_, i) => i !== idx));
    if (formError?.kind === "upload") setFormError(null);
  };

  // ── Q11 geo-tagged visit photos ───────────────────────────────────────────
  // Each photo keeps its own GPS; one without GPS gets the device's current
  // location stamped in (the portal's usual behaviour for event photos), so a
  // visit photo always reaches the server with coordinates.
  const addVisitFiles = async (files) => {
    if (!files.length || visitUploading) return;
    const remaining = VISIT_PHOTOS_MAX - visitPhotos.length;
    if (remaining <= 0) {
      raise({ q: "Q11", label: "Q11", msg: `You can upload at most ${VISIT_PHOTOS_MAX} photos of the visit. Remove one to add another.`, kind: "upload" });
      return;
    }
    const picked = files.slice(0, remaining);
    setFormError(null);
    setVisitUploading(picked.length);
    try {
      const added = [];
      for (const file of picked) {
        const ext = (file.name.split(".").pop() || "").toLowerCase();
        if (!["image/jpeg", "image/jpg", "image/png"].includes(file.type) || !["jpg", "jpeg", "png"].includes(ext)) {
          throw new Error(`"${file.name}" is not allowed. Upload JPG, JPEG or PNG photos.`);
        }
        const tagged = await processGeotaggedImage(file, PHOTO_MAX_MB, { fallbackToDeviceLocation: true, section: "shs26_visit" });
        const formData = new FormData();
        formData.append("files", tagged.processedFile);
        const res = await axiosConn("post", "upload-collaboration-file", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        const url = res?.data?.status === "success" ? (res.data.data || [])[0] : null;
        if (!url) throw new Error(res?.data?.message || "Upload failed. Please try again.");
        added.push({ url, name: file.name, latitude: tagged.latitude, longitude: tagged.longitude, geoTagAdded: !!tagged.geoTagAdded });
      }
      setVisitPhotos((prev) => [...prev, ...added].slice(0, VISIT_PHOTOS_MAX));
    } catch (err) {
      // processGeotaggedImage rejects with a `reason` when location is denied
      const denied = err && err.reason ? "Allow location access so the photo can be geo-tagged, then try again." : null;
      raise({ q: "Q11", label: "Q11", msg: denied || err?.response?.data?.message || err?.message || "Upload failed. Please try again.", kind: "upload" });
    } finally {
      setVisitUploading(0);
    }
  };

  const removeVisitPhoto = (idx) => {
    setVisitPhotos((prev) => prev.filter((_, i) => i !== idx));
    if (formError?.kind === "upload") setFormError(null);
  };

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting || uploading || visitUploading) return;
    const problem = validate(form, photos, visitPhotos);
    if (problem) {
      raise(problem);
      jump(problem.q);
      return;
    }
    setFormError(null);
    try {
      const res = await submitSurvey({
        ...form,
        awarenessOther: form.awarenessOther.trim(),
        competitionOther: form.competitionOther.trim(),
        reductionOther: form.reductionOther.trim(),
        photos,
        visitPlace: form.visitPlace.trim(),
        visitDescription: form.visitDescription.trim(),
        visitPhotos: form.exposureVisit === "yes" ? visitPhotos : [],
      }).unwrap();
      onSuccess && onSuccess(res);
    } catch (err) {
      raise({ q: null, label: "", msg: err?.data?.message || "Could not save the report. Please check your connection and try again.", kind: "server" });
    }
  };

  const full = photos.length >= PHOTOS_MAX;
  const RING = 2 * Math.PI * 19;

  const dropTitle = uploading
    ? "Uploading your files…"
    : full
      ? `All ${PHOTOS_MAX} files added`
      : dragging
        ? "Drop the files here"
        : "Drag photos here or click to browse";

  return (
    <div ref={rootRef} className={`shsf-form ${armed ? "is-armed" : ""}`}>
      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <div className="shsf-hero">
        <div className="shsf-hero__stripe" aria-hidden="true" />
        <FaRecycle className="shsf-hero__ring" aria-hidden="true" />
        {LEAVES.map((s, k) => (
          <span key={k} className="shsf-leaf" style={s} aria-hidden="true" />
        ))}

        <div className="shsf-hero__top">
          <button type="button" className="shsf-back" onClick={onBack}>
            <FaArrowLeft />
            <span>{backLabel}</span>
          </button>
          <span className="shsf-hero__chip">
            <i aria-hidden="true" />
            {isUpdate ? "Update your report" : "School report"}
          </span>
        </div>

        <div className="shsf-hero__main">
          <div className="shsf-hero__logo">
            <img src={shsLogo} alt={SURVEY_TITLE} />
          </div>
          <div className="shsf-hero__copy">
            <span className="shsf-hero__kicker">Solid Waste Management Drive</span>
            <h1 className="shsf-hero__title">
              <span>Swachhata Hi Seva</span>
              <span className="shsf-hero__year">2026</span>
            </h1>
            <p className="shsf-hero__tagline">{SURVEY_TAGLINE}</p>
            <p className="shsf-hero__sub">
              {isUpdate
                ? "Review your school's Solid Waste Management report and update anything that has changed."
                : "Tell us how your school took part. It takes about five minutes, and your participation certificate is issued as soon as you submit."}
            </p>
          </div>
          <HeroBins />
        </div>

        <svg className="shsf-wave shsf-wave--back" viewBox="0 0 2400 80" preserveAspectRatio="none" aria-hidden="true">
          <path d={WAVE_PATH} />
        </svg>
        <svg className="shsf-wave" viewBox="0 0 2400 80" preserveAspectRatio="none" aria-hidden="true">
          <path d={WAVE_PATH} />
        </svg>
      </div>

      {/* ── School profile ───────────────────────────────────────────────── */}
      <div className="shsf-school">
        <span className="shsf-school__lock">
          <FaLock aria-hidden="true" />
          From your school profile
        </span>
        {[
          [FaSchool, "School name", schoolName],
          [FaIdCard, "UDISE code", udise],
          [FaMapMarkerAlt, "State / District", place],
        ].map(([Icon, label, value]) => (
          <div className="shsf-school__tile" key={label}>
            <span className="shsf-school__icon" aria-hidden="true">
              <Icon />
            </span>
            <span className="shsf-school__text">
              <span className="shsf-school__label">{label}</span>
              <span className="shsf-school__value" title={String(value)}>
                {value}
              </span>
            </span>
          </div>
        ))}
      </div>

      {/* ── Sticky progress ──────────────────────────────────────────────── */}
      <div className={`shsf-progress ${complete ? "is-complete" : ""}`}>
        <div className="shsf-ring" role="img" aria-label={`${done} of ${QIDS.length} questions answered`}>
          <svg viewBox="0 0 48 48" aria-hidden="true">
            <circle className="shsf-ring__bg" cx="24" cy="24" r="19" />
            <circle className="shsf-ring__fg" cx="24" cy="24" r="19" strokeDasharray={RING} strokeDashoffset={RING * (1 - done / QIDS.length)} />
          </svg>
          <b>{complete ? <Tick /> : done}</b>
        </div>
        <div className="shsf-progress__text">
          <b>{complete ? "All questions answered" : `${done} of ${QIDS.length} questions answered`}</b>
          <span>{complete ? `Review your answers, then ${isUpdate ? "update" : "submit"} the report` : "Tap a number to jump to that question"}</span>
        </div>
        <ol className="shsf-dots">
          {QIDS.map((q) => (
            <li key={q}>
              <button
                type="button"
                className={`shsf-dot ${progress[q] ? "is-done" : ""} ${shownError?.q === q ? "is-error" : ""}`}
                onClick={() => jump(q)}
                aria-label={`Go to question ${q.slice(1)}`}
              >
                {q.slice(1)}
              </button>
            </li>
          ))}
        </ol>
        <span className="shsf-progress__bar" aria-hidden="true">
          <i style={{ width: `${pct}%` }} />
        </span>
      </div>

      {/* ── Questions ────────────────────────────────────────────────────── */}
      <form id="shsf-survey-form" className="shsf-body" onSubmit={handleSubmit} noValidate>
        <Section step="Part 1" title="Bins and awareness" icon={<FaBullhorn />} ids={["Q1", "Q2", "Q3"]} progress={progress}>
          <Question id="Q1" n="Q1" text={QUESTIONS.q1} done={progress.Q1} error={errorFor("Q1")} errorKey={nonce}>
            <BinTiles value={form.binsInstalled} onChange={set("binsInstalled")} />
          </Question>

          <Question id="Q2" n="Q2" text={QUESTIONS.q2} done={progress.Q2} error={errorFor("Q2")} errorKey={nonce}>
            <Tiles name="awarenessConducted" options={YES_NO} value={form.awarenessConducted} onChange={set("awarenessConducted")} />
            {form.awarenessConducted === "yes" && (
              <Question id="Q2A" n="Q2A" text={QUESTIONS.q2a} hint="Select all that apply" done={listDone(form.awarenessTypes, form.awarenessOther)} sub>
                <Chips options={AWARENESS_TYPES} value={form.awarenessTypes} onChange={set("awarenessTypes")} />
                {form.awarenessTypes.includes("other") && (
                  <OtherInput value={form.awarenessOther} onChange={set("awarenessOther")} placeholder="Describe the other awareness activity" />
                )}
              </Question>
            )}
          </Question>

          <Question id="Q3" n="Q3" text={QUESTIONS.q3} hint="Select all that apply" done={progress.Q3} error={errorFor("Q3")} errorKey={nonce}>
            <Chips options={COMPETITIONS} value={form.competitions} onChange={set("competitions")} />
            {form.competitions.includes("other") && (
              <OtherInput value={form.competitionOther} onChange={set("competitionOther")} placeholder="Describe the other competition or activity" />
            )}
          </Question>
        </Section>

        <Section step="Part 2" title="Participation" icon={<FaUsers />} ids={["Q4", "Q5", "Q6"]} progress={progress}>
          <div className="shsf-counts">
            {COUNTS.map(({ id, key, text, Icon, tone }) => (
              <Question
                key={key}
                id={id}
                n={id}
                text={text}
                done={progress[id]}
                error={errorFor(id)}
                errorKey={nonce}
                className={`shsf-count shsf-count--${tone}`}
                deco={
                  <span className="shsf-count__icon" aria-hidden="true">
                    <Icon />
                  </span>
                }
              >
                <div className="shsf-stepper">
                  <span key={form[key]} className="shsf-stepper__flash" aria-hidden="true" />
                  <button type="button" onClick={() => bump(key, -1)} disabled={form[key] === "" || form[key] === "0"} aria-label={`Decrease: ${text}`}>
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M6 12h12" />
                    </svg>
                  </button>
                  <input type="text" inputMode="numeric" placeholder="0" value={form[key]} onChange={setCount(key)} aria-label={text} />
                  <button type="button" onClick={() => bump(key, 1)} aria-label={`Increase: ${text}`}>
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M12 6v12M6 12h12" />
                    </svg>
                  </button>
                </div>
              </Question>
            ))}
          </div>
        </Section>

        <Section step="Part 3" title="Waste practices" icon={<FaRecycle />} ids={["Q7", "Q8", "Q9"]} progress={progress}>
          <Question id="Q7" n="Q7" text={QUESTIONS.q7} done={progress.Q7} error={errorFor("Q7")} errorKey={nonce}>
            <Tiles name="wasteAudit" options={YES_NO} value={form.wasteAudit} onChange={set("wasteAudit")} />
          </Question>

          <Question id="Q8" n="Q8" text={QUESTIONS.q8} done={progress.Q8} error={errorFor("Q8")} errorKey={nonce}>
            <Tiles name="wasteReduction" options={YES_NO} value={form.wasteReduction} onChange={set("wasteReduction")} />
            {form.wasteReduction === "yes" && (
              <Question id="Q8A" n="Q8A" text={QUESTIONS.q8a} hint="Select all that apply" done={listDone(form.reductionActivities, form.reductionOther)} sub>
                <Chips options={REDUCTION_ACTIVITIES} value={form.reductionActivities} onChange={set("reductionActivities")} />
                {form.reductionActivities.includes("other") && (
                  <OtherInput value={form.reductionOther} onChange={set("reductionOther")} placeholder="Describe the other activity" />
                )}
              </Question>
            )}
          </Question>

          <Question id="Q9" n="Q9" text={QUESTIONS.q9} done={progress.Q9} error={errorFor("Q9")} errorKey={nonce}>
            <Tiles name="wetWasteComposted" options={COMPOST_OPTIONS} value={form.wetWasteComposted} onChange={set("wetWasteComposted")} />
          </Question>
        </Section>

        <Section step="Part 4" title="Photographs" icon={<FaCamera />} ids={["Q10"]} progress={progress}>
          <Question
            id="Q10"
            n="Q10"
            text={QUESTIONS.q10}
            hint={`${PHOTOS_MIN} to ${PHOTOS_MAX} files, JPG, JPEG, PNG or PDF, up to ${PHOTO_MAX_MB} MB each`}
            done={progress.Q10}
            error={errorFor("Q10")}
            errorKey={nonce}
          >
            <div
              className={`shsf-drop ${dragging ? "is-drag" : ""} ${full ? "is-full" : ""} ${uploading ? "is-busy" : ""}`}
              onDragOver={(e) => {
                e.preventDefault();
                if (!full && !uploading) setDragging(true);
              }}
              onDragLeave={() => setDragging(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragging(false);
                if (!full && !uploading) addFiles(Array.from(e.dataTransfer.files || []));
              }}
            >
              <label className="shsf-drop__zone">
                <input type="file" accept="image/png,image/jpeg,application/pdf" multiple onChange={handleFiles} disabled={!!uploading || full} />
                <span className="shsf-drop__icon" aria-hidden="true">
                  {uploading ? (
                    <span className="shsf-spinner shsf-spinner--lg" />
                  ) : full ? (
                    <Tick />
                  ) : (
                    <svg viewBox="0 0 40 40" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M12 29H10.5a7.5 7.5 0 0 1-1-14.9A10 10 0 0 1 29 12.6 7 7 0 0 1 29.5 29H28" />
                      <g className="shsf-drop__arrow">
                        <path d="M20 34V19M14.5 24.5 20 19l5.5 5.5" />
                      </g>
                    </svg>
                  )}
                </span>
                <span className="shsf-drop__title">{dropTitle}</span>
                <span className="shsf-drop__meta">{full ? "Remove a file to replace it" : `${photos.length} of ${PHOTOS_MAX} added, at least ${PHOTOS_MIN} needed`}</span>
                {!full && !uploading && <span className="shsf-drop__btn">Choose files</span>}
              </label>

              <div className="shsf-slots">
                {Array.from({ length: PHOTOS_MAX }, (_, i) => {
                  const p = photos[i];
                  if (p) {
                    return (
                      <div className="shsf-slot is-filled" key={p.url}>
                        {p.type === "pdf" ? (
                          <a className="shsf-slot__pdf" href={p.url} target="_blank" rel="noopener noreferrer" title={p.name || "PDF"}>
                            <FaFilePdf />
                            <span>PDF</span>
                          </a>
                        ) : (
                          <img src={p.url} alt={p.name || `Photo ${i + 1}`} />
                        )}
                        <span className="shsf-slot__badge">{`File ${i + 1}`}</span>
                        <span className="shsf-slot__name">{p.name || `File ${i + 1}`}</span>
                        <span className="shsf-slot__actions">
                          {p.type !== "pdf" && (
                            <button type="button" onClick={() => setLightbox(p.url)} aria-label="View photo">
                              <FaExpand />
                            </button>
                          )}
                          <button type="button" className="is-remove" onClick={() => removePhoto(i)} aria-label="Remove file">
                            <FaTimes />
                          </button>
                        </span>
                      </div>
                    );
                  }
                  if (uploading && i < photos.length + uploading) {
                    return <div className="shsf-slot is-loading" key={`l${i}`} aria-hidden="true" />;
                  }
                  return (
                    <div className={`shsf-slot is-empty ${i < PHOTOS_MIN ? "is-required" : ""}`} key={`e${i}`}>
                      <FaImage aria-hidden="true" />
                      <span>{`File ${i + 1}`}</span>
                      <small>{i < PHOTOS_MIN ? "Required" : "Optional"}</small>
                    </div>
                  );
                })}
              </div>
            </div>
          </Question>
        </Section>

        {/* Q11 — exposure visit (added at the States' request, 2026-09-21) */}
        <Section step="Part 5" title="Exposure visit" icon={<FaBus />} ids={["Q11"]} progress={progress}>
          <Question id="Q11" n="Q11" text={QUESTIONS.q11} done={progress.Q11} error={errorFor("Q11")} errorKey={nonce}>
            <Tiles name="exposureVisit" options={YES_NO} value={form.exposureVisit} onChange={set("exposureVisit")} />
            {form.exposureVisit === "yes" && (
              <div className="shsf-visit">
                <label className="shsf-visit__field">
                  <span className="shsf-visit__label">{QUESTIONS.q11Place}</span>
                  <input
                    className="shsf-input"
                    type="text"
                    maxLength={200}
                    placeholder="e.g. Municipal composting plant, material recovery facility, STP"
                    value={form.visitPlace}
                    onChange={(e) => set("visitPlace")(e.target.value)}
                  />
                </label>

                <label className="shsf-visit__field">
                  <span className="shsf-visit__label">{QUESTIONS.q11Description}</span>
                  <textarea
                    className="shsf-input shsf-visit__text"
                    rows={4}
                    maxLength={VISIT_DESCRIPTION_MAX}
                    placeholder="What the students saw and learnt during the visit"
                    value={form.visitDescription}
                    onChange={(e) => set("visitDescription")(e.target.value)}
                  />
                  <small className="shsf-visit__count">{`${form.visitDescription.length} / ${VISIT_DESCRIPTION_MAX}`}</small>
                </label>

                <div className="shsf-visit__counts">
                  <div className="shsf-visit__field">
                    <span className="shsf-visit__label">{QUESTIONS.q11Students}</span>
                <div className="shsf-stepper">
                  <span key={form.visitStudents} className="shsf-stepper__flash" aria-hidden="true" />
                  <button type="button" onClick={() => bump("visitStudents", -1)} disabled={form.visitStudents === "" || form.visitStudents === "0"} aria-label={`Decrease: ${QUESTIONS.q11Students}`}>
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M6 12h12" />
                    </svg>
                  </button>
                  <input type="text" inputMode="numeric" placeholder="0" value={form.visitStudents} onChange={setCount("visitStudents")} aria-label={QUESTIONS.q11Students} />
                  <button type="button" onClick={() => bump("visitStudents", 1)} aria-label={`Increase: ${QUESTIONS.q11Students}`}>
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M12 6v12M6 12h12" />
                    </svg>
                  </button>
                </div>
                  </div>
                  <div className="shsf-visit__field">
                    <span className="shsf-visit__label">{QUESTIONS.q11Teachers}</span>
                <div className="shsf-stepper">
                  <span key={form.visitTeachers} className="shsf-stepper__flash" aria-hidden="true" />
                  <button type="button" onClick={() => bump("visitTeachers", -1)} disabled={form.visitTeachers === "" || form.visitTeachers === "0"} aria-label={`Decrease: ${QUESTIONS.q11Teachers}`}>
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M6 12h12" />
                    </svg>
                  </button>
                  <input type="text" inputMode="numeric" placeholder="0" value={form.visitTeachers} onChange={setCount("visitTeachers")} aria-label={QUESTIONS.q11Teachers} />
                  <button type="button" onClick={() => bump("visitTeachers", 1)} aria-label={`Increase: ${QUESTIONS.q11Teachers}`}>
                    <svg viewBox="0 0 24 24" aria-hidden="true">
                      <path d="M12 6v12M6 12h12" />
                    </svg>
                  </button>
                </div>
                  </div>
                </div>

                <div className="shsf-visit__field">
                  <span className="shsf-visit__label">{QUESTIONS.q11Photos}</span>
                  <small className="shsf-visit__hint">
                    {`${VISIT_PHOTOS_MIN} to ${VISIT_PHOTOS_MAX} photos, JPG or PNG. A photo without location gets your device's current location added — please allow location access.`}
                  </small>
                  <div className={`shsf-drop ${visitPhotos.length >= VISIT_PHOTOS_MAX ? "is-full" : ""} ${visitUploading ? "is-busy" : ""}`}>
                    <label className="shsf-drop__zone">
                      <input
                        type="file"
                        accept="image/png,image/jpeg"
                        multiple
                        onChange={(e) => {
                          const files = Array.from(e.target.files || []);
                          e.target.value = "";
                          addVisitFiles(files);
                        }}
                        disabled={!!visitUploading || visitPhotos.length >= VISIT_PHOTOS_MAX}
                      />
                      <span className="shsf-drop__icon" aria-hidden="true">
                        {visitUploading ? <span className="shsf-spinner shsf-spinner--lg" /> : <FaMapMarkerAlt />}
                      </span>
                      <span className="shsf-drop__title">
                        {visitUploading ? "Geo-tagging and uploading…" : visitPhotos.length >= VISIT_PHOTOS_MAX ? "All photos added" : "Add geo-tagged photos of the visit"}
                      </span>
                      <span className="shsf-drop__meta">{`${visitPhotos.length} of ${VISIT_PHOTOS_MAX} added, at least ${VISIT_PHOTOS_MIN} needed`}</span>
                      {!visitUploading && visitPhotos.length < VISIT_PHOTOS_MAX && <span className="shsf-drop__btn">Choose photos</span>}
                    </label>

                    <div className="shsf-slots">
                      {Array.from({ length: VISIT_PHOTOS_MAX }, (_, i) => {
                        const p = visitPhotos[i];
                        if (p) {
                          return (
                            <div className="shsf-slot is-filled" key={p.url}>
                              <img src={p.url} alt={p.name || `Visit photo ${i + 1}`} />
                              <span className="shsf-slot__badge">{`Photo ${i + 1}`}</span>
                              <span className="shsf-slot__geo" title={p.geoTagAdded ? "Location added from this device" : "Location read from the photo"}>
                                {`📍 ${Number(p.latitude).toFixed(4)}, ${Number(p.longitude).toFixed(4)}`}
                              </span>
                              <span className="shsf-slot__actions">
                                <button type="button" onClick={() => setLightbox(p.url)} aria-label="View photo">
                                  <FaExpand />
                                </button>
                                <button type="button" className="is-remove" onClick={() => removeVisitPhoto(i)} aria-label="Remove photo">
                                  <FaTimes />
                                </button>
                              </span>
                            </div>
                          );
                        }
                        if (visitUploading && i < visitPhotos.length + visitUploading) {
                          return <div className="shsf-slot is-loading" key={`vl${i}`} aria-hidden="true" />;
                        }
                        return (
                          <div className={`shsf-slot is-empty ${i < VISIT_PHOTOS_MIN ? "is-required" : ""}`} key={`ve${i}`}>
                            <FaImage aria-hidden="true" />
                            <span>{`Photo ${i + 1}`}</span>
                            <small>{i < VISIT_PHOTOS_MIN ? "Required" : "Optional"}</small>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </Question>
        </Section>
      </form>

      {/* ── Sticky footer — outside the <form> so it sticks from the first screen ── */}
      <div className="shsf-footer">
          <div className="shsf-footer__status">
            {shownError ? (
              <button type="button" key={nonce} className="shsf-footer__error" onClick={() => shownError.q && jump(shownError.q)}>
                <span className="shsf-footer__bang" aria-hidden="true">
                  !
                </span>
                <span>{shownError.q ? `${shownError.label}: ${shownError.msg}` : shownError.msg}</span>
              </button>
            ) : (
              <div className="shsf-footer__meter">
                <span className="shsf-footer__track" aria-hidden="true">
                  <i style={{ width: `${pct}%` }} />
                </span>
                <span>{complete ? "Everything is filled in" : `${done} of ${QIDS.length} answered`}</span>
              </div>
            )}
          </div>
          <button type="submit" form="shsf-survey-form" className={`shsf-submit ${complete && !submitting ? "is-ready" : ""}`} disabled={submitting || !!uploading}>
            {submitting && <span className="shsf-spinner" aria-hidden="true" />}
            <span>{submitting ? "Saving…" : uploading ? "Uploading…" : isUpdate ? "Update report" : "Submit report"}</span>
            {!submitting && (
              <svg className="shsf-submit__arrow" viewBox="0 0 20 20" aria-hidden="true">
                <path d="M4 10h12M11 5l5 5-5 5" />
              </svg>
            )}
          </button>
        </div>

      {lightbox && (
        <div className="ekped3-lightbox" onClick={() => setLightbox(null)}>
          <button type="button" className="ekped3-lightbox-close" aria-label="Close" onClick={() => setLightbox(null)}>
            ×
          </button>
          <img src={lightbox} alt="Preview" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </div>
  );
}
